/**
 * Environment Variables Validation
 * 
 * Validates required environment variables at runtime
 * to catch configuration errors early.
 */

const requiredEnvVars = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
] as const;

export interface EnvConfig {
  supabaseUrl: string;
  supabaseAnonKey: string;
  sentryDsn?: string;
  haBridgeUrl?: string;
  telegramBotToken?: string;
  claudeApiKey?: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Validates required environment variables and throws if any are missing
 */
export function validateEnv(): void {
  // CI/E2E modunda BYPASS_AUTH aktifse Supabase zorunlu değil
  if (import.meta.env.VITE_BYPASS_AUTH === 'true') {
    return;
  }

  const missing = requiredEnvVars.filter(
    key => !import.meta.env[key] || import.meta.env[key] === ''
  );
  
  if (missing.length > 0) {
    const errorMessage = [
      '❌ Missing required environment variables:',
      '',
      ...missing.map(key => `  - ${key}`),
      '',
      '📝 Setup instructions:',
      '  1. Copy .env.example to .env',
      '  2. Fill in the required values',
      '  3. Restart the development server',
      '',
      '💡 See .env.example for details',
    ].join('\n');
    
    console.error(errorMessage);
    throw new Error(`Missing environment variables: ${missing.join(', ')}`);
  }
}

/**
 * Gets validated environment configuration
 */
export function getEnvConfig(): EnvConfig {
  validateEnv();
  
  const config: EnvConfig = {
    supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    sentryDsn: import.meta.env.VITE_SENTRY_DSN || undefined,
    haBridgeUrl: import.meta.env.VITE_HA_BRIDGE_URL || undefined,
    // ⚠️ SECURITY: VITE_ prefix'li değerler Vite tarafından JS bundle'a statik olarak yazılır.
    // VITE_TELEGRAM_BOT_TOKEN ve VITE_CLAUDE_API_KEY production'da Supabase Edge Function
    // secrets olarak saklanmalı; client bundle'a açık kalmamalıdır.
    // Geçici kullanım için burada bırakılmıştır — production hardening ile server-side proxy'e taşıyın.
    telegramBotToken: import.meta.env.VITE_TELEGRAM_BOT_TOKEN || undefined,
    claudeApiKey: import.meta.env.VITE_CLAUDE_API_KEY || undefined,
    isDevelopment: import.meta.env.DEV,
    isProduction: import.meta.env.PROD,
  };
  
  // Log configuration status (without sensitive values)
  if (config.isDevelopment) {
    console.log('🔧 Environment Configuration:');
    console.log('  ✅ Supabase:', config.supabaseUrl ? 'configured' : 'missing');
    console.log('  ℹ️  Sentry:', config.sentryDsn ? 'configured' : 'not configured');
    console.log('  ℹ️  HA Bridge:', config.haBridgeUrl ? 'configured' : 'not configured');
    console.log('  ℹ️  Telegram:', config.telegramBotToken ? 'configured' : 'not configured');
    console.log('  ℹ️  Claude AI:', config.claudeApiKey ? 'configured' : 'not configured');
  }
  
  return config;
}

/**
 * Checks if a specific feature is enabled based on env vars
 */
export function isFeatureEnabled(feature: keyof EnvConfig): boolean {
  const config = getEnvConfig();
  return !!config[feature];
}
